/**
 * Replit Environment Utilities
 * 
 * This module provides utilities for handling Replit-specific environment issues,
 * particularly Vite host restrictions which can prevent the application from loading.
 */

// Check if we're running in the Replit environment
export const isReplitEnvironment = (): boolean => {
  // Check if we're running in the browser first
  if (typeof window === 'undefined') return false;
  
  const { hostname } = window.location;
  return hostname.includes('.replit.dev') || 
         hostname.includes('.repl.co') || 
         hostname.includes('worf.replit.dev') ||
         hostname === 'staymidle.com';
};

// Apply Replit-specific fixes for Vite host restrictions
export const applyReplitViteFixes = (): void => {
  if (!isReplitEnvironment()) return;
  
  // Set cookies that might help bypass host restrictions
  document.cookie = "vite-host-bypass=true; path=/; max-age=3600";
  
  // Set localStorage items
  try {
    localStorage.setItem('vite-host-allowed', 'true');
    localStorage.setItem('vite-bypass-host', 'true');
    sessionStorage.setItem('vite-allow-host', 'true');
  } catch (err) {
    console.warn('Unable to set storage items for Vite bypass:', err);
  }
  
  // Add meta tags to help with host detection
  const metaAllowHosts = document.createElement('meta');
  metaAllowHosts.name = 'vite-allow-host';
  metaAllowHosts.content = window.location.hostname;
  document.head.appendChild(metaAllowHosts);
  
  // Log info about environment
  console.info(`Replit environment detected: ${window.location.hostname}`);
  console.info('Applied Vite host restriction fixes');
};

// Fallback redirect for when Vite fails to load
export const redirectToDirectAccess = (): void => {
  const directAccessUrl = `${window.location.origin}/direct-access`;
  console.warn(`Redirecting to direct access page: ${directAccessUrl}`);
  window.location.href = directAccessUrl;
};

// Check if Vite has loaded successfully
export const checkViteLoaded = (): boolean => {
  // A simple heuristic to check if Vite has loaded
  return (
    document.querySelector('script[type="module"]') !== null &&
    !document.body.textContent?.includes('Blocked request') &&
    !document.body.textContent?.includes('is not allowed')
  );
};

// Initialize Replit environment fixes
export const initReplitEnvironment = (): void => {
  if (!isReplitEnvironment()) return;
  
  // Apply fixes immediately
  applyReplitViteFixes();
  
  // Check if Vite loaded properly after a delay
  setTimeout(() => {
    if (!checkViteLoaded()) {
      console.warn('Vite does not appear to have loaded properly');
      
      // If body contains host restriction error, redirect
      if (document.body.textContent?.includes('Blocked request') ||
          document.body.textContent?.includes('is not allowed')) {
        redirectToDirectAccess();
      }
    }
  }, 3000);
};

export default {
  isReplitEnvironment,
  applyReplitViteFixes,
  redirectToDirectAccess,
  checkViteLoaded,
  initReplitEnvironment
};